import { Component, OnDestroy, OnInit, ViewEncapsulation, ViewChild, Inject, PLATFORM_ID, Input } from '@angular/core';
import {
    ShellDomainProvider,
    EcapLoginService,
    LanguageAction,
    getCurrentLanguage as GetCurrentLanguage,
    State,
    EcapCookieService
} from '../../../../../core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { find } from 'lodash';
import { FuseConfigService } from './../../../../@fuse/services/config.service';
import { FuseSidebarService } from './../../../../@fuse/components/sidebar/sidebar.service';
import { navigation } from './../../../navigation/navigation';
import { isPlatformBrowser } from '@angular/common';
import { EcapBroadcastService } from './../../../../ecap/services/ecap-broadcast.service';

@Component({
    selector: 'toolbar',
    templateUrl: './toolbar.component.html',
    styleUrls: ['./toolbar.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ToolbarComponent implements OnInit, OnDestroy {
    @ViewChild('authFormLogout', { static: true }) authFormLogout;
    @Input() userInfo: any;

    fuseConfig: any;
    currentDomain: string;

    horizontalNavbar: boolean;
    rightNavbar: boolean;
    hiddenNavbar: boolean;
    showShortcuts: boolean;
    showSearch: boolean;
    showUserInfo: boolean;
    languages: any;
    navigation: any;
    selectedLanguage: any;
    userStatusOptions: any[];
    basicUserInfo: any;

    language = new Subject<string>();
    lang$: Observable<string>;

    // Private
    private _unsubscribeAll: Subject<void>;

    /**
     * Constructor
     *
     * param {FuseConfigService} _fuseConfigService
     * param {FuseSidebarService} _fuseSidebarService
     * param {TranslateService} _translateService
     */
    constructor(
        @Inject(PLATFORM_ID) private platformId: Object,
        private _fuseConfigService: FuseConfigService,
        private _fuseSidebarService: FuseSidebarService,
        private _translateService: TranslateService,
        private loginService: EcapLoginService,
        private ecapBroadcastService: EcapBroadcastService,
        private translationService: TranslateService,
        private _cookieService: EcapCookieService,
        private store: Store<State>
    ) {
        // Set the defaults
        this.userStatusOptions = [
            {
                title: 'Online',
                icon: 'icon-checkbox-marked-circle',
                color: '#4CAF50'
            },
            {
                title: 'Away',
                icon: 'icon-clock',
                color: '#FFC107'
            },
            {
                title: 'Do not Disturb',
                icon: 'icon-minus-circle',
                color: '#F44336'
            },
            {
                title: 'Invisible',
                icon: 'icon-checkbox-blank-circle-outline',
                color: '#BDBDBD'
            },
            {
                title: 'Offline',
                icon: 'icon-checkbox-blank-circle-outline',
                color: '#616161'
            }
        ];

        this.languages = [
            {
                id: 'en',
                title: 'English',
                flag: 'us'
            },
            {
                id: 'tr',
                title: 'Turkish',
                flag: 'tr'
            }
        ];

        this.navigation = navigation;

        // Set the private defaults
        this._unsubscribeAll = new Subject();

        this.lang$ = this.store.select(GetCurrentLanguage);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to the config changes
        this._fuseConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(settings => {
            this.fuseConfig = settings;
            this.horizontalNavbar = settings.layout.navbar.position === 'top';
            this.rightNavbar = settings.layout.navbar.position === 'right';
            this.hiddenNavbar = settings.layout.navbar.hidden === true;
            this.showShortcuts = settings.layout.toolbar.component.shortcuts;
            this.showSearch = settings.layout.toolbar.component.search;
            this.showUserInfo = settings.layout.toolbar.component.userInfo;

            if (
                settings.layout.toolbar.component &&
                settings.layout.toolbar.component.languageMenu &&
                settings.layout.toolbar.component.languageMenu.languageList &&
                settings.layout.toolbar.component.languageMenu.languageList.length > 0
            ) {
                this.languages = settings.layout.toolbar.component.languageMenu.languageList;
            }
        });

        // Set the selected language from default languages
        // this.selectedLanguage = find(this.languages, { 'id': this._translateService.currentLang });

        this.currentDomain = window.location.protocol + '//' + window.location.hostname;
        this.authFormLogout.nativeElement.action = this.currentDomain + '/auth?LogOff';
        this.ecapBroadcastService.onUserInfoEvent().subscribe(userInfo => {
            this.userInfo = userInfo;
            if (userInfo.ProfileImage) {
                this.basicUserInfo = JSON.parse(JSON.stringify(userInfo));
            }
        });

        this.lang$.subscribe(lang => {
            this.selectedLanguage = find(this.languages, { id: this._translateService.currentLang });
            this.translationService.use(lang);
        });

        this.language.subscribe(lang => {
            this.store.dispatch(new LanguageAction(lang));
            this.selectedLanguage = find(this.languages, { id: this._translateService.currentLang });
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * param key
     */
    toggleSidebarOpen(key): void {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
    }

    /**
     * Search
     *
     * param value
     */
    search(value): void {
        // Do your search here...
        // console.log(value);
    }

    /**
     * Set the language
     *
     * param lang
     */
    public setLanguage(lang): void {
        // Set the selected language for the toolbar
        // this.selectedLanguage = lang;

        // Use the selected language for translations
        this._translateService.use(lang.id);
        this.language.next(lang.id);
        this.selectedLanguage = lang;
        this.setLangCookie(lang.id);
    }

    public setLangCookie(lang) {
        this._cookieService.eraseAndSetCookie('langKey', lang);
    }

    clickOnProfile(): void {
        if (this.fuseConfig.layout.toolbar.component.userMenu.clickOnProfileEvent) {
            this.fuseConfig.layout.toolbar.component.userMenu.clickOnProfileEvent();
        }
    }

    public logout() {
        if (ShellDomainProvider['api']['isSSo'] && isPlatformBrowser(this.platformId)) {
            const landingUri = ShellDomainProvider['api']['landingUri'] || this.currentDomain;
            const siteUrl = window.location.hostname;
            let ssoDomains = siteUrl;
            let may_access = ShellDomainProvider['api']['ssoDomains'] || localStorage.getItem('may_access');

            //#region //? SSO config with Ignored domain
            const ssoIgnoreDomains = ShellDomainProvider['api']['ssoIgnoreDomains'];
            if (ssoIgnoreDomains && ssoIgnoreDomains.length) {
                let accessDomainList = Array.isArray(may_access) ? may_access : may_access.split(',');
                accessDomainList = accessDomainList.filter((el: string) => !ssoIgnoreDomains.includes(el));
                accessDomainList = accessDomainList.filter((el: string) => siteUrl !== el);
                may_access = accessDomainList.join(',');
            }
            //#endregion
            this._cookieService.eraseAndSetCookie('ecap-user-roles', JSON.stringify([]));

            if (may_access && may_access !== 'undefined' && may_access !== 'null') {
                ssoDomains += ',' + may_access;
            }

            this.authFormLogout.nativeElement[0].value = landingUri;
            this.authFormLogout.nativeElement[1].value = ssoDomains;
            this.authFormLogout.nativeElement.submit();
        } else {
            this.loginService.logoutFromPlatform();
        }
    }
}
